import { useCallback } from 'react';
import { allowedNotificationTypes, phoneNotificationTypes, tagNotificationsType } from '@frontend/settings-routing';
import {
  PopupDuration,
  PopupDurationValues,
  SoundId,
  SoundSettings,
  TransientQueueProps,
  WeavePopNotification,
} from '@frontend/types';
import { usePreference } from '@frontend/user-preferences';

export const getSoundSettings = () => {
  const settings: Record<any, SoundId | ''> = {};
  [...allowedNotificationTypes, ...phoneNotificationTypes, ...tagNotificationsType].forEach((notification) => {
    settings[notification.type] = '';
  });
  return settings as SoundSettings;
};

const isValidDuration = (value: unknown): value is PopupDurationValues => {
  return value === PopupDuration.Manual || value === PopupDuration.FourThousand || value === PopupDuration.TenThousand;
};

const isValidPlacement = (value: unknown): value is NonNullable<TransientQueueProps['placement']> => {
  return value === 'top-right' || value === 'top-left' || value === 'bottom-right' || value === 'bottom-left';
};

export const useNotificationSettings = () => {
  const [notificationSettings, setNotificationSettings] = usePreference('NotificationSettings');
  const safeDurationMs = isValidDuration(notificationSettings?.notificationSettings?.durationMs)
    ? notificationSettings.notificationSettings.durationMs
    : PopupDuration.FourThousand;
  const safePlacement = isValidPlacement(notificationSettings?.notificationSettings?.placement)
    ? notificationSettings.notificationSettings.placement
    : 'top-right';

  const safeNotificationSettings = {
    durationMs: safeDurationMs,
    placement: safePlacement,
  };

  const setManageAll = useCallback(
    (value: boolean): void => {
      setNotificationSettings({ ...notificationSettings, manageAll: value });
    },
    [notificationSettings]
  );

  const setNotificationSound = useCallback(
    ({ type, soundId }: { soundId: SoundId; type: WeavePopNotification['type'] }): void => {
      setNotificationSettings({
        ...notificationSettings,
        notificationSounds: { ...notificationSettings.notificationSounds, [type]: soundId },
      });
    },
    [notificationSettings]
  );

  const setDurationMs = useCallback(
    (durationMs: PopupDurationValues): void => {
      setNotificationSettings({
        ...notificationSettings,
        notificationSettings: { ...safeNotificationSettings, durationMs },
        hasChangedDuration: true,
      });
    },
    [notificationSettings, safeNotificationSettings]
  );

  const setPlacement = useCallback(
    (placement: TransientQueueProps['placement']): void => {
      setNotificationSettings({
        ...notificationSettings,
        notificationSettings: { ...safeNotificationSettings, placement },
      });
    },
    [notificationSettings, safeNotificationSettings]
  );

  return {
    notificationSettings: safeNotificationSettings,
    notificationSounds: notificationSettings.notificationSounds,
    manageAll: notificationSettings.manageAll,
    hasChangedDuration: !!notificationSettings.hasChangedDuration,
    setManageAll,
    setNotificationSound,
    setDurationMs,
    setPlacement,
  };
};
